import * as ko from 'knockout';

import { links } from '../links';
import AuthenticatedUser from '../authenticatedUser';
import AuthenticationClient from '../common/webHostClient/authentication/authenticationClient';
import ConfigurationClient from '../common/webHostClient/configuration/configurationClient';
import GetTokenInfoRequest from '../common/webHostClient/authentication/getTokenInfoRequest';
import GetTokenInfoResponse from '../common/webHostClient/authentication/getTokenInfoResponse';
import storage from '../common/storage';
import globals from '../common/globals';
import '../common/staticFileReferences';
import Feature from '../common/webHostClient/configuration/Feature';

/**
 * Base page view model
 */
export default abstract class BasePage {

    private static wayleavesMapFeatureName  = "WayleavesMapUSOnline";
    private static exclusionZoneMapFeatureName  = "ExclusionZoneMapUSOnline";
    public readonly currentUser: ko.Observable<AuthenticatedUser> = ko.observable();
    public readonly links = links;
    protected requiresAuthentication: boolean = false;
    public readonly isWayleavesMapEnabled : ko.Observable<boolean> = ko.observable(false);
    public readonly isExclusionZoneMapEnabled : ko.Observable<boolean> = ko.observable(false);

    public async initialiseAsync(): Promise<void> {

        // Get site key
        const configurationClient = new ConfigurationClient();
        globals.config = await configurationClient.getGeneralConfigurationAsync();

        if (!await this.preflightAsync())
        {
            console.log("Page preflight checks failed, navigating to home page.");
            window.location.assign(this.links.homeHref);
            return;
        }

        await this.loadHeadAsync();
        await this.loadAuthenticationAsync();

        if (this.requiresAuthentication && this.currentUser() == null)
        {
            console.log("Page requires user to login.");
            const loginHref: string = this.links.getRedirectingBackToCurrentPageLoginHref("login");
            window.location.assign(loginHref);
            return;
        }

        const wayleavesMapFeatureToggle : Feature =  globals.config.features.find(i => i.name === BasePage.wayleavesMapFeatureName)
        const exclusionZoneMapFeatureToggle : Feature =  globals.config.features.find(i => i.name === BasePage.exclusionZoneMapFeatureName)

        this.isWayleavesMapEnabled(wayleavesMapFeatureToggle.enabled);
        this.isExclusionZoneMapEnabled(exclusionZoneMapFeatureToggle.enabled)

        await this.loadContentAsync();

        this.defineCustomBindings();

        ko.applyBindings(this);

        await this.onLoadCompleteAsync();
    }

    protected async onLoadCompleteAsync(): Promise<void>  {
        return;
    }

    protected async loadContentAsync(): Promise<void>
    {
        return;
    }

    public signOut() {
        this.clearAllInProgressOrderData();
        storage.clearAuthenticationToken();
        window.location.assign(this.links.homeHref);
    }

    protected async preflightAsync(): Promise<boolean> {
        return true;
    }

    protected getQueryStringParameter(name) {
        return decodeURIComponent((new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|#|;|$)').exec(window.location.search) || [null, ''])[1].replace(/\+/g, '%20')) || null;
    }

    /**
     * Clears all in progress order data from persistent storage.
     */
    protected clearAllInProgressOrderData() {
        storage.clearInProgressOrderToken();
        storage.clearPayPalOrderId();
        storage.clearStartOrderLocationQueryText();
        storage.clearStartOrderMapBounds();
        storage.clearStartOrderSiteGeometry();
    }

    private initialiseGooleTagManager() {
        
        const id = 'GTM-T7Q28RD';

        window['dataLayer'] = window['dataLayer']||[];
        window['dataLayer'].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js'
        });

        const gtmScriptElement = document.createElement('script')
        gtmScriptElement.async=true
        gtmScriptElement.src='https://www.googletagmanager.com/gtm.js'+
            '?id=' + id + 
            '&gtm_auth=' + globals.config.gtmAuth + 
            '&gtm_preview=' + globals.config.gtmPreview + 
            '&gtm_cookies_win=x';

        const firstScriptElement = document.getElementsByTagName('script')[0];
        firstScriptElement.parentNode.insertBefore(gtmScriptElement, firstScriptElement);
    }

    private async loadHeadAsync() {

        const headElement = document.getElementsByTagName("head")[0];

        // Google Tag Manager
        this.initialiseGooleTagManager();

        // Google reCAPTCHA
        const recaptchaScriptElement: HTMLScriptElement = document.createElement("script");
        recaptchaScriptElement.src = `${globals.config.reCaptchaClientScriptUrl}?render=${globals.config.reCaptchaSiteKey}`;
        
        await new Promise((resolve, reject) => {
            recaptchaScriptElement.addEventListener('load', () => resolve(null));
            recaptchaScriptElement.onerror = (error) => reject(error);
            headElement.appendChild(recaptchaScriptElement);
        });


        // Infinity call tracking
        try {
            (function (i:any, n, f, t, y, x, z) {
                y = i._its = function () { return y.queue.push(arguments) }; y.version = '2.0'; y.queue = []; z = n.createElement(f); z.async = !0; z.src = t; x = n.getElementsByTagName(f)[0]; x.parentNode.insertBefore(z, x)
            })(window, document, 'script', 'https://script.infinity-tracking.com/infinitytrack.js?i=269');
            window['_its']('init', '269');
            window['_its']('track');
        }
        catch (err) {
            console.log("Failed to initialise Infinity Call Tracking", err);
        }
        
    }

    /**
     * Loads authentication items for the page.
     */
    private async loadAuthenticationAsync() {

        const authenticationClient: AuthenticationClient = new AuthenticationClient();

        const tokenInfoRequest: GetTokenInfoRequest = {
            orderToken: storage.getInProgressOrderToken(),
        };

        const tokenInfoResponse : GetTokenInfoResponse = await authenticationClient.getTokenInfo(tokenInfoRequest);

        // Is the user authenticated?
        if (tokenInfoResponse.currentUserEmail && tokenInfoResponse.currentUserName)
        {
            this.currentUser({
                name: tokenInfoResponse.currentUserName, 
                email: tokenInfoResponse.currentUserEmail,
            });
        }
        else
        {
            storage.clearAuthenticationToken();
            this.currentUser(null);
        }

        // Is there an order in progress?
        if (tokenInfoResponse.orderToken)
        {
            // Yes, hang on to the refreshed token
            storage.setInProgressOrderToken(tokenInfoResponse.orderToken);
        }
        else
        {
            // No, clear any stale token.
            storage.clearInProgressOrderToken();
        }
    }

    private defineCustomBindings() {

        // Some popover elements are created by knockout, we need to wait until knockout has finished binding before initialising them.
        // Using a databinding to bind popover instead of doing it in the view models.
        ko.bindingHandlers.bindPopover = {
            init: (element, _valueAccessor, _allBindings, _viewModel, _bindingContext) => {
                $(element).popover();
            }
        };

        ko.bindingHandlers.fadeVisible = {
            init: function(element, valueAccessor) {
                // Initially set the element to be instantly visible/hidden depending on the value
                var value = valueAccessor();
                $(element).toggle(ko.unwrap(value)); // Use "unwrapObservable" so we can handle values that may or may not be observable
            },
            update: function(element, valueAccessor) {
                // Whenever the value subsequently changes, slowly fade the element in or out
                var value = valueAccessor();
                ko.unwrap(value) ? $(element).fadeIn() : $(element).hide();
            }
        }

        ko.bindingHandlers.toggleSelectOptionsPopovers = {
            init: (element, valueAccessor, allBindings, viewModel, bindingContext) => {
                if (!valueAccessor()) {
                    return;
                }

                if (valueAccessor().notAvailableUtilitySearchSummary()) {
                    $("[data-toggle=popoverUSS]").popover({ trigger: "hover click", delay: 375 });
                } else {
                    // Remove any existing popover
                    $("[data-toggle=popoverUSS]").popover("destroy");

                    // Stop popover from appearing
                    $("[data-toggle=popoverUSS]").popover({ trigger: "manual" });
                }

                if (valueAccessor().notAvailableUtilityLegalView() || valueAccessor().usmDisablesUlv()) {
                    $("[data-toggle=popoverULV]").popover({ trigger: "hover click", delay: 375 });
                } else {
                    // Remove any existing popover
                    $("[data-toggle=popoverULV]").popover("destroy");

                    // Stop popover from appearing
                    $("[data-toggle=popoverULV]").popover({ trigger: "manual" });
                }

                if (valueAccessor().notAvailableExclusionZoneMap() || valueAccessor().usmDisablesEzm()) {
                    $("[data-toggle=popoverEZM]").popover({ trigger: "hover click", delay: 375 });
                } else {
                    // Remove any existing popover
                    $("[data-toggle=popoverEZM]").popover("destroy");

                    // Stop popover from appearing
                    $("[data-toggle=popoverEZM]").popover({ trigger: "manual" });
                }
            }
        }
        
    }
}